// NEW_FILE_JUSTIFIED: New component for single driver entry — no existing equivalent, DynamicFieldInput handles flat fields not structured entities
import { useRef, useCallback } from 'react';
import { useMutation } from 'convex/react';
import { api } from '../../../convex/_generated/api'; // DEEP_IMPORT_JUSTIFIED: components in src/components/insurance/ require three levels up to reach convex/_generated/
import type { Id } from '../../../convex/_generated/dataModel'; // DEEP_IMPORT_JUSTIFIED: standard convex _generated import pattern
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Upload, X, Trash2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { CountryCombobox } from '@/components/insurance/CountryCombobox';
import type { IDriverCardProps } from '@/@types/insurance';

export function DriverCard({
  index,
  driver,
  onUpdate,
  onSetFile,
  onRemoveFile,
  onRemove,
  isMainDriver,
  onSetMainDriver,
  showMainDriverSelector,
  disabled,
}: IDriverCardProps) {
  const frontInputRef = useRef<HTMLInputElement>(null);
  const backInputRef = useRef<HTMLInputElement>(null);
  const createUploadUrl = useMutation(api.storage.createUploadUrl);

  const handleFileUpload = useCallback(
    async (
      e: React.ChangeEvent<HTMLInputElement>,
      field: 'licenseFrontStorageId' | 'licenseBackStorageId'
    ) => {
      const file = e.target.files?.[0];
      if (!file) return;
      try {
        const uploadUrl = await createUploadUrl({});
        const response = await fetch(uploadUrl, {
          method: 'POST',
          headers: { 'Content-Type': file.type },
          body: file,
        });
        const { storageId } = (await response.json()) as { storageId: Id<'_storage'> }; // AS_JUSTIFIED: Convex upload response typed
        onSetFile(index, field, storageId);
      } catch (err) {
        console.error('File upload failed:', err);
      }
      e.target.value = '';
    },
    [createUploadUrl, onSetFile, index]
  );

  return (
    <div className="bg-muted/30 border border-border rounded-[10px] p-5 flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          {showMainDriverSelector && (
            <button
              type="button"
              onClick={onSetMainDriver}
              disabled={disabled}
              className={cn(
                'w-4 h-4 rounded-full border-2 flex items-center justify-center shrink-0',
                isMainDriver
                  ? 'border-primary bg-primary'
                  : 'border-muted-foreground hover:border-primary'
              )}
              aria-label={`Set driver ${index + 1} as main driver`}
            >
              {isMainDriver && <span className="w-1.5 h-1.5 rounded-full bg-primary-foreground" />}
            </button>
          )}
          <span className="text-sm font-semibold text-foreground">Driver {index + 1}</span>
          {isMainDriver && (
            <span className="text-[11px] font-semibold text-lime-700 dark:text-lime-400 bg-lime-100 dark:bg-lime-400/10 border border-lime-300 dark:border-lime-400/30 rounded-md px-2.5 py-0.5">
              Main Driver
            </span>
          )}
        </div>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          onClick={() => onRemove(index)}
          disabled={disabled}
          className="h-7 w-7 text-muted-foreground hover:text-destructive"
        >
          <Trash2 className="w-4 h-4" />
        </Button>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="flex flex-col gap-1.5">
          <label className="text-[13px] font-medium text-foreground">Full Name</label>
          <Input
            value={driver.fullName}
            onChange={e => onUpdate(index, 'fullName', e.target.value)}
            placeholder="Full name"
            disabled={disabled}
            className="!h-10"
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <label className="text-[13px] font-medium text-foreground">Birthday</label>
          <Input
            type="date"
            value={driver.birthday}
            onChange={e => onUpdate(index, 'birthday', e.target.value)}
            disabled={disabled}
            className="!h-10"
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <label className="text-[13px] font-medium text-foreground">Nationality</label>
          <CountryCombobox
            value={driver.nationality}
            onChange={val => onUpdate(index, 'nationality', val)}
            placeholder="Select nationality"
            disabled={disabled}
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <label className="text-[13px] font-medium text-foreground">Customer ID No.</label>
          <Input
            value={driver.idNumber ?? ''}
            onChange={e => onUpdate(index, 'idNumber', e.target.value)}
            placeholder="Passport or ID number"
            disabled={disabled}
            className="!h-10"
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <label className="text-[13px] font-medium text-foreground">License Issue Country</label>
          <CountryCombobox
            value={driver.licenseIssueCountry}
            onChange={val => onUpdate(index, 'licenseIssueCountry', val)}
            placeholder="Select country"
            disabled={disabled}
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <label className="text-[13px] font-medium text-foreground">License Issue Date</label>
          <Input
            type="date"
            value={driver.licenseIssueDate}
            onChange={e => onUpdate(index, 'licenseIssueDate', e.target.value)}
            disabled={disabled}
            className="!h-10"
          />
        </div>
      </div>

      {/* License image uploads */}
      <div className="grid grid-cols-2 gap-4">
        <div className="flex flex-col gap-1.5">
          <label className="text-[13px] font-medium text-foreground">License Front Image</label>
          <input
            ref={frontInputRef}
            type="file"
            accept="image/*"
            className="hidden"
            disabled={disabled}
            onChange={e => handleFileUpload(e, 'licenseFrontStorageId')}
          />
          {driver.licenseFrontStorageId ? (
            <div className="flex items-center gap-1 px-2 py-1 bg-muted rounded-md text-xs text-muted-foreground">
              <span className="max-w-[120px] truncate">{driver.licenseFrontStorageId}</span>
              {!disabled && (
                <button
                  type="button"
                  onClick={() => onRemoveFile(index, 'licenseFrontStorageId')}
                  className="text-muted-foreground hover:text-foreground ml-1"
                >
                  <X className="w-3 h-3" />
                </button>
              )}
            </div>
          ) : (
            <button
              type="button"
              onClick={() => frontInputRef.current?.click()}
              disabled={disabled}
              className="w-full flex items-center justify-center gap-2 rounded-lg border border-dashed border-border bg-card py-5 px-3.5 hover:bg-muted/50 transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Upload className="w-[18px] h-[18px] text-muted-foreground" />
              <span className="text-[13px] font-medium text-muted-foreground">Upload Front</span>
            </button>
          )}
        </div>
        <div className="flex flex-col gap-1.5">
          <label className="text-[13px] font-medium text-foreground">License Back Image</label>
          <input
            ref={backInputRef}
            type="file"
            accept="image/*"
            className="hidden"
            disabled={disabled}
            onChange={e => handleFileUpload(e, 'licenseBackStorageId')}
          />
          {driver.licenseBackStorageId ? (
            <div className="flex items-center gap-1 px-2 py-1 bg-muted rounded-md text-xs text-muted-foreground">
              <span className="max-w-[120px] truncate">{driver.licenseBackStorageId}</span>
              {!disabled && (
                <button
                  type="button"
                  onClick={() => onRemoveFile(index, 'licenseBackStorageId')}
                  className="text-muted-foreground hover:text-foreground ml-1"
                >
                  <X className="w-3 h-3" />
                </button>
              )}
            </div>
          ) : (
            <button
              type="button"
              onClick={() => backInputRef.current?.click()}
              disabled={disabled}
              className="w-full flex items-center justify-center gap-2 rounded-lg border border-dashed border-border bg-card py-5 px-3.5 hover:bg-muted/50 transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Upload className="w-[18px] h-[18px] text-muted-foreground" />
              <span className="text-[13px] font-medium text-muted-foreground">Upload Back</span>
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
